import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import * as React from 'react';

import { cn } from '@ui/lib/utils';
import { ZIndex } from '@ui/zIndexUtils';

type PopoverProps = PopoverPrimitive.Root.Props;

const Popover = ({ ...props }: PopoverProps) => <PopoverPrimitive.Root data-slot="popover" {...props} />;

type PopoverTriggerProps = PopoverPrimitive.Trigger.Props;

const PopoverTrigger = ({ ...props }: PopoverTriggerProps) => (
  <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
);

type PopoverContentProps = PopoverPrimitive.Popup.Props &
  Pick<PopoverPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'anchor' | 'side' | 'sideOffset'> & {
    positionerZIndex?: number;
  };

const PopoverContent = ({
  className,
  positionerZIndex = ZIndex.POPUP,
  align = 'center',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  anchor,
  ...props
}: PopoverContentProps) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Positioner
      align={align}
      alignOffset={alignOffset}
      side={side}
      sideOffset={sideOffset}
      anchor={anchor}
      className="isolate z-50"
      style={{ zIndex: positionerZIndex }}
    >
      <PopoverPrimitive.Popup
        data-slot="popover-content"
        className={cn(
          'z-50 flex w-72 origin-(--transform-origin) flex-col gap-2.5 rounded-lg',
          'bg-popover p-2.5 text-sm text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-hidden',
          'duration-100',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2',
          'data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95',
          'data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          className
        )}
        {...props}
      />
    </PopoverPrimitive.Positioner>
  </PopoverPrimitive.Portal>
);

type PopoverHeaderProps = React.ComponentProps<'div'>;

const PopoverHeader = ({ className, ...props }: PopoverHeaderProps) => (
  <div data-slot="popover-header" className={cn('flex flex-col gap-0.5 text-sm', className)} {...props} />
);

type PopoverTitleProps = PopoverPrimitive.Title.Props;

const PopoverTitle = ({ className, ...props }: PopoverTitleProps) => (
  <PopoverPrimitive.Title data-slot="popover-title" className={cn('font-medium', className)} {...props} />
);

type PopoverDescriptionProps = PopoverPrimitive.Description.Props;

const PopoverDescription = ({ className, ...props }: PopoverDescriptionProps) => (
  <PopoverPrimitive.Description
    data-slot="popover-description"
    className={cn('text-muted-foreground', className)}
    {...props}
  />
);

export { Popover, PopoverContent, PopoverDescription, PopoverHeader, PopoverTitle, PopoverTrigger };
